import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ParametresFerme } from '../../models/parametres.model';
import { FermeStateService } from './ferme-state.service';

export type ImageFerme = 'signature' | 'cachet' | 'logo';

/** Paramètres de la ferme, chargés une fois et partagés par tous les écrans */
@Injectable({ providedIn: 'root' })
export class ParametresService {
  private base = `${environment.BASE_URL}/api/parametres`;
  private cache$: Observable<ParametresFerme> | null = null;
  private courant$ = new BehaviorSubject<ParametresFerme | null>(null);
  /** Change à chaque envoi ou retrait du logo : la barre latérale recharge l'image */
  private logoVersion$ = new BehaviorSubject<string>(String(Date.now()));

  constructor(private http: HttpClient, private fermeState: FermeStateService) {}

  get snapshot(): ParametresFerme | null { return this.courant$.getValue(); }

  get(): Observable<ParametresFerme> {
    if (!this.cache$) {
      this.cache$ = this.http.get<ParametresFerme>(this.base, { params: { fermeId: this.fermeState.fermeId } }).pipe(
        tap(p => this.courant$.next(p)),
        catchError(() => { this.cache$ = null; return of(null as any); }),
        shareReplay(1)
      );
    }
    return this.cache$;
  }

  save(p: ParametresFerme): Observable<ParametresFerme> {
    return this.http.put<ParametresFerme>(this.base, p, { params: { fermeId: this.fermeState.fermeId } }).pipe(
      tap(saved => { this.courant$.next(saved); this.cache$ = of(saved).pipe(shareReplay(1)); })
    );
  }

  /** Image de signature ou de cachet (PNG/JPEG) */
  televerserImage(role: ImageFerme, fichier: Blob, nom: string): Observable<ParametresFerme> {
    const fd = new FormData();
    fd.append('fichier', fichier, nom);
    return this.http.post<ParametresFerme>(`${this.base}/image/${role}`, fd, { params: { fermeId: this.fermeState.fermeId } }).pipe(
      tap(saved => { this.courant$.next(saved); this.cache$ = of(saved).pipe(shareReplay(1)); if (role === 'logo') { this.logoChange(); } })
    );
  }

  supprimerImage(role: ImageFerme): Observable<ParametresFerme> {
    return this.http.delete<ParametresFerme>(`${this.base}/image/${role}`, { params: { fermeId: this.fermeState.fermeId } }).pipe(
      tap(saved => { this.courant$.next(saved); this.cache$ = of(saved).pipe(shareReplay(1)); if (role === 'logo') { this.logoChange(); } })
    );
  }

  urlImage(role: ImageFerme, version?: string): string {
    return `${this.base}/image/${role}?fermeId=${this.fermeState.fermeId}&v=${encodeURIComponent(version || '')}`;
  }

  /** Adresse du logo de la ferme (téléversé, sinon ancien logo de la fiche ferme), rechargée après chaque changement */
  urlLogo(): Observable<string> {
    return this.logoVersion$.pipe(map(v => this.urlImage('logo', v)));
  }

  private logoChange(): void { this.logoVersion$.next(String(Date.now())); }

  /** Mode de paiement par défaut : paramètre de la ferme, sinon Wave */
  modeDefaut(): Observable<string> {
    return this.get().pipe(map(p => p?.modePaiementDefaut || 'Wave'));
  }
}
